import styled from "@emotion/styled";
import { DateTime } from "luxon";
import {
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
  MDSProgressBar,
  MDSProgressBarColor,
  MDSSkeleton,
  MDSSkeletonProps,
  MDSButton,
  MDSButtonSlotName,
} from "@/mds";
import { CollapsibleSectionHeader } from "@/components/heads-up/cards/common/collapsible-section-header";
import { SmartCardSection, NoteList, SeeMoreRow } from "@/components/heads-up/cards/common/layout";
import { formatRelativeDayLabel } from "@/components/heads-up/cards/common/date-utils";
import {
  ConnectorColumn,
  ConnectorTop,
  ConnectorBottom,
  ConnectorDot,
  TimelineContent,
  TimelineDateText,
  NoteRow,
  LeadingIcon,
} from "@/components/heads-up/cards/common";
import type { FC } from "react";
import { useHeadsUpUnlockThreshold } from "../hooks/use-heads-up-unlock-threshold";
import { pluralize } from "@/modules/pluralize";
import { useImportModal } from "@/contexts/ImportModalContext";

interface HeadsUpLockedViewProps {
  untrashedNoteCount: number;
}

export const HeadsUpLockedView: FC<HeadsUpLockedViewProps> = ({ untrashedNoteCount }) => {
  const { threshold: unlockThreshold, notesRemaining } = useHeadsUpUnlockThreshold({
    nullableUntrashedNoteCount: untrashedNoteCount,
  });

  const { setIsImportModalOpen } = useImportModal();

  // Generate skeleton timeline dates
  const timelineDates = [
    DateTime.now().minus({ days: 1 }),
    DateTime.now().minus({ days: 13 }),
    DateTime.now().minus({ days: 51 }),
  ];

  return (
    <Container data-component="HeadsUpLockedView">
      <SkeletonContent data-test-id="skeleton-content">
        <SmartCardSection>
          <SkeletonBar height={24} width="60%" disableAnimation />
          <SkeletonBar height={16} width="50%" disableAnimation />
          <CollapsibleSectionHeader title="MEETING TIMELINE" defaultCollapsed={false}>
            <TimelineList>
              {timelineDates.map((date, index) => (
                <div key={index} style={{ position: "relative" }}>
                  <TimelineRow>
                    <ConnectorColumn>
                      <ConnectorTop $visible={index !== 0}>
                        <line
                          x1="1"
                          y1="0"
                          x2="1"
                          y2="100%"
                          stroke="currentColor"
                          strokeWidth={1}
                          strokeDasharray="2 2"
                        />
                      </ConnectorTop>
                      <ConnectorDot />
                      <ConnectorBottom $visible={index !== timelineDates.length - 1}>
                        <line
                          x1="1"
                          y1="0"
                          x2="1"
                          y2="100%"
                          stroke="currentColor"
                          strokeWidth={1}
                          strokeDasharray="2 2"
                        />
                      </ConnectorBottom>
                    </ConnectorColumn>
                    <TimelineContent>
                      <TimelineDateText
                        variant={MDSTextVariant.TextXs}
                        weight={MDSTextWeight.SemiBold}
                      >
                        {formatRelativeDayLabel(date.toISO() ?? "")}
                      </TimelineDateText>
                      <SkeletonBar height={12} width="60%" />
                      <SkeletonBar height={12} width="40%" />
                      <NoteChipContainer>
                        <MDSIcon
                          kind={MDSIconKind.Document}
                          color={MDSIconColor.Secondary}
                          size={11}
                        />
                        <SkeletonBar height={12} width="80px" />
                      </NoteChipContainer>
                    </TimelineContent>
                  </TimelineRow>
                </div>
              ))}
              <SeeMoreRow as="div">
                <MDSText
                  variant={MDSTextVariant.TextXs}
                  weight={MDSTextWeight.Medium}
                  color={MDSTextColor.Secondary}
                >
                  See all
                </MDSText>
              </SeeMoreRow>
            </TimelineList>
          </CollapsibleSectionHeader>
        </SmartCardSection>

        <SmartCardSection>
          <CollapsibleSectionHeader title="MENTIONED BY" defaultCollapsed={false}>
            <NoteList>
              {[1, 2].map(index => (
                <div key={index} style={{ position: "relative" }}>
                  <NoteRow>
                    <LeadingIcon $isDoubleRow={false}>
                      <MDSIcon
                        kind={MDSIconKind.Document}
                        color={MDSIconColor.Secondary}
                        size={11}
                      />
                    </LeadingIcon>
                    <NoteContentContainer>
                      <SkeletonBar height={12} width="70%" />
                    </NoteContentContainer>
                  </NoteRow>
                </div>
              ))}
            </NoteList>
          </CollapsibleSectionHeader>
        </SmartCardSection>
      </SkeletonContent>

      <UnlockSection data-test-id="unlock-section">
        <UnlockSectionTextWrapper>
          <MDSText
            variant={MDSTextVariant.TextM}
            weight={MDSTextWeight.Smedium}
            color={MDSTextColor.Secondary}
          >
            Create {notesRemaining} {pluralize(notesRemaining, "note", "notes")} to unlock{" "}
            <NoWrap>Heads Up</NoWrap>
          </MDSText>
          <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
            When unlocked, Heads Up will automatically surface related notes and connections as you
            work.
          </MDSText>
        </UnlockSectionTextWrapper>
        <ProgressContainer>
          <MDSProgressBar
            minValue={0}
            maxValue={unlockThreshold}
            value={untrashedNoteCount}
            color={MDSProgressBarColor.Emphasis}
            size="xlarge"
            rounded
          />
          <ProgressText
            variant={MDSTextVariant.TextSm}
            weight={MDSTextWeight.Smedium}
            color={MDSTextColor.Primary}
            data-test-id="heads-up-locked-view-progress-text"
          >
            {untrashedNoteCount}/{unlockThreshold} created
          </ProgressText>
        </ProgressContainer>
        <MDSButton
          aria-label="Import"
          onClick={() => {
            setIsImportModalOpen(true);
          }}
        >
          <MDSIcon
            aria-label="Arrow Up"
            slot={MDSButtonSlotName.LeftIcon}
            kind={MDSIconKind.Import}
            size={16}
          />
          Import notes instead
        </MDSButton>
      </UnlockSection>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100%;
  position: relative;
`;

const SkeletonContent = styled.div`
  flex: 1;
  overflow-y: hidden;
  position: relative;
  pointer-events: none;
  max-height: 550px;

  /* Fade out effect at the bottom */
  mask-image: linear-gradient(to bottom, black 0%, black calc(100% - 120px), transparent 100%);
  -webkit-mask-image: linear-gradient(
    to bottom,
    black 0%,
    black calc(100% - 120px),
    transparent 100%
  );
`;

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2px;
`;

const StyledSkeletonBar = styled(MDSSkeleton)`
  margin-top: 4px;
`;

const SkeletonBar = ({ disableAnimation = true, ...props }: MDSSkeletonProps) => (
  <StyledSkeletonBar {...props} disableAnimation={disableAnimation} />
);

const NoteChipContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  margin-left: -1px;
`;

const NoteContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const UnlockSection = styled.div`
  display: flex;
  align-self: center;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  margin: 0 40px 40px 40px;
  max-width: 400px;
`;

const UnlockSectionTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding: ${({ theme }) => theme.spacing["3"]};
  text-align: center;
  orphans: 3;
  widows: 3;
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["3"]};
  margin-bottom: ${({ theme }) => theme.spacing["6"]};
`;

const ProgressText = styled(MDSText)`
  white-space: nowrap;
`;

export const TimelineRow = styled.div`
  position: relative;
  display: flex;
  gap: ${({ theme }) => theme.spacing["1"]};
  min-height: 50px;
  padding: 0px 10.5px;
  border-radius: 8px;
`;

const NoWrap = styled.span`
  white-space: nowrap;
`;
